'use client';

import * as React from 'react';

import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '../../components/ui/dialog';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '../../components/ui/drawer';
import { useMediaQuery } from '../../lib/hooks/use-media-query';
import { cn } from '../../lib/utils';

interface BaseProps {
	children: React.ReactNode;
}

interface RootCredenzaProps extends BaseProps {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

interface CredenzaProps extends BaseProps {
	className?: string;
	asChild?: true;
	closeButtonColor?: string;
	hideClose?: boolean;
	dialogTitle?: string;
	ignoreClickOutsideComponent?: boolean;
	/** Drawer only: floats the drag handle over the content so a full-bleed header can reach the top edge. */
	overlayHandle?: boolean;
}

const desktop = '(min-width: 768px)';

const CredenzaContext = React.createContext<{ isDialog: boolean } | null>(null);

const useCredenzaContext = () => {
	const ctx = React.useContext(CredenzaContext);
	if (!ctx) {
		throw new Error('Credenza subcomponents must be used within <Credenza>');
	}
	return ctx;
};

const Credenza = ({ children, ...props }: RootCredenzaProps) => {
	const { value: isDesktop, isInitializingState } = useMediaQuery(desktop);
	const isDialog = isInitializingState || isDesktop;
	const Root = isDialog ? Dialog : Drawer;
	return (
		<CredenzaContext.Provider value={{ isDialog }}>
			<Root {...props}>{children}</Root>
		</CredenzaContext.Provider>
	);
};

const CredenzaTrigger = ({ className, children, ...props }: CredenzaProps) => {
	const { isDialog } = useCredenzaContext();
	const Trigger = isDialog ? DialogTrigger : DrawerTrigger;
	return (
		<Trigger className={className} {...props}>
			{children}
		</Trigger>
	);
};

const CredenzaClose = ({ className, children, ...props }: CredenzaProps) => {
	const { isDialog } = useCredenzaContext();
	const Close = isDialog ? DialogClose : DrawerClose;
	return (
		<Close className={className} {...props}>
			{children}
		</Close>
	);
};

const CredenzaContent = ({
	className,
	children,
	closeButtonColor,
	hideClose = false,
	dialogTitle,
	ignoreClickOutsideComponent,
	overlayHandle,
	...props
}: CredenzaProps) => {
	const { isDialog } = useCredenzaContext();

	if (isDialog) {
		return (
			<DialogContent
				onInteractOutside={(e) => {
					if (ignoreClickOutsideComponent) {
						e.preventDefault();
					}
				}}
				onEscapeKeyDown={(e) => {
					if (ignoreClickOutsideComponent) {
						e.preventDefault();
					}
				}}
				className={className}
				closeButtonColor={closeButtonColor}
				hideClose={hideClose}
				{...props}
			>
				{dialogTitle ? <DialogTitle className="sr-only">{dialogTitle}</DialogTitle> : null}
				{children}
			</DialogContent>
		);
	}

	return (
		<DrawerContent
			onInteractOutside={(e) => {
				if (ignoreClickOutsideComponent) {
					e.preventDefault();
				}
			}}
			onEscapeKeyDown={(e) => {
				if (ignoreClickOutsideComponent) {
					e.preventDefault();
				}
			}}
			className={className}
			overlayHandle={overlayHandle}
			{...props}
		>
			{children}
		</DrawerContent>
	);
};

const CredenzaDescription = ({ className, children, ...props }: CredenzaProps) => {
	const { isDialog } = useCredenzaContext();
	const Description = isDialog ? DialogDescription : DrawerDescription;
	return (
		<Description className={className} {...props}>
			{children}
		</Description>
	);
};

const CredenzaHeader = ({ className, children, ...props }: CredenzaProps) => {
	const { isDialog } = useCredenzaContext();
	const Header = isDialog ? DialogHeader : DrawerHeader;
	return (
		<Header className={className} {...props}>
			{children}
		</Header>
	);
};

const CredenzaTitle = ({ className, children, ...props }: CredenzaProps) => {
	const { isDialog } = useCredenzaContext();
	const Title = isDialog ? DialogTitle : DrawerTitle;
	return (
		<Title className={className} {...props}>
			{children}
		</Title>
	);
};

const CredenzaBody = ({ className, children, ...props }: CredenzaProps) => {
	return (
		<div className={cn('px-4 md:px-0', className)} {...props}>
			{children}
		</div>
	);
};

const CredenzaFooter = ({ className, children, ...props }: CredenzaProps) => {
	const { isDialog } = useCredenzaContext();
	const Footer = isDialog ? DialogFooter : DrawerFooter;
	return (
		<Footer className={className} {...props}>
			{children}
		</Footer>
	);
};

export {
	Credenza,
	CredenzaBody,
	CredenzaClose,
	CredenzaContent,
	CredenzaDescription,
	CredenzaFooter,
	CredenzaHeader,
	CredenzaTitle,
	CredenzaTrigger
};
